import React, { useState, useEffect, useMemo } from 'react';
import { Plug, Loader, CheckCircle, XCircle, Trash2, FlaskConical, Plus, RefreshCw, Search } from 'lucide-react';
import {
  listIntegrations, createIntegration, deleteIntegration, testIntegration, syncIntegration, discoverMcp, getOrganisations,
} from '../services/api';
import SlackPanel from '../components/SlackPanel';

// Inspect the JWT once to decide which Slack scope to show. super_admin gets
// the Rupadi-internal channel config; everyone else gets their own org's.
function decodeRole() {
  try {
    const t = localStorage.getItem('admin_token');
    return JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).role || '';
  } catch { return ''; }
}

const TYPES = [
  { value: 'rest_api', label: 'Generic REST / JSON API' },
  { value: 'ats', label: 'ATS (named provider)' },
  { value: 'internal_db', label: 'Internal company system (API)' },
  { value: 'mcp', label: 'MCP server (HTTP)' },
];

const REQ_FIELDS = ['jobTitle', 'department', 'jobLocation', 'employmentType', 'salaryRange', 'experienceLevel', 'skills', 'external_id'];

// Presets for named ATS vendors — pre-fill the REST connection with the vendor's
// known API shape. The admin still supplies the API key. AI extraction is on by
// default since ATS payloads have nested fields.
const ATS_PRESETS = {
  greenhouse: { label: 'Greenhouse (Harvest)', base_url: 'https://harvest.greenhouse.io/v1', method: 'GET', auth_type: 'basic', req_path: '/jobs', req_items_key: '', cand_path: '/candidates', note: 'Use your Harvest API key as the Basic-auth username (leave password blank).' },
  lever: { label: 'Lever', base_url: 'https://api.lever.co/v1', method: 'GET', auth_type: 'basic', req_path: '/postings', req_items_key: 'data', cand_path: '/opportunities', note: 'Use your Lever API key as the Basic-auth username (blank password).' },
  ashby: { label: 'Ashby', base_url: 'https://api.ashbyhq.com', method: 'POST', auth_type: 'basic', req_path: '/jobPosting.list', req_items_key: 'results', cand_path: '/candidate.list', note: 'API key as Basic-auth username; Ashby uses POST list endpoints.' },
  workable: { label: 'Workable', base_url: 'https://www.workable.com/spi/v3', method: 'GET', auth_type: 'bearer', req_path: '/jobs', req_items_key: 'jobs', cand_path: '/candidates', note: 'Bearer token; for a subaccount use https://{sub}.workable.com/spi/v3.' },
  recruitee: { label: 'Recruitee', base_url: 'https://api.recruitee.com/c/{company_id}', method: 'GET', auth_type: 'bearer', req_path: '/offers', req_items_key: 'offers', cand_path: '/candidates', note: 'Bearer token; replace {company_id} in the URL.' },
};

const blankForm = () => ({
  name: '', type: 'rest_api', provider: '', organization_id: '',
  base_url: '', method: 'GET',
  auth_type: 'none', token: '', api_key: '', header_name: 'X-API-Key', username: '', password: '',
  req_path: '', req_items_key: '', req_mapping: {},
  pull_candidates: false, cand_path: '', cand_items_key: '',
  use_llm: false, llm_instructions: '',
  headers_json: '', query_json: '', body_json: '',
  sync_enabled: false, sync_interval: 60,
  mcp_req_tool: '', mcp_req_args: '', mcp_cand_tool: '', mcp_cand_args: '',
});

// Parse a JSON-object textarea; '' -> undefined, invalid -> throws with a label.
function parseJsonObj(text, label) {
  const t = (text || '').trim();
  if (!t) return undefined;
  let v;
  try { v = JSON.parse(t); } catch { throw new Error(`${label} must be valid JSON.`); }
  if (typeof v !== 'object' || Array.isArray(v)) throw new Error(`${label} must be a JSON object.`);
  return v;
}

function IntegrationsList() {
  const [connections, setConnections] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState({});
  const [mcpTools, setMcpTools] = useState(null); // discovered tool list (form)
  const [discovering, setDiscovering] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [conns, orgs] = await Promise.all([listIntegrations(), getOrganisations().catch(() => [])]);
      setConnections(conns || []);
      setOrganisations(orgs || []);
    } catch {
      setError('Failed to load connections.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const orgName = (id) => organisations.find((o) => o.organisation_id === id)?.name || id || '—';
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setMap = (field, v) => setForm((f) => ({ ...f, req_mapping: { ...f.req_mapping, [field]: v } }));

  const applyAtsPreset = (key) => {
    const p = ATS_PRESETS[key];
    if (!p) { set('provider', ''); return; }
    setForm((f) => ({
      ...f, provider: key, base_url: p.base_url, method: p.method, auth_type: p.auth_type,
      req_path: p.req_path, req_items_key: p.req_items_key,
      use_llm: true, pull_candidates: !!p.cand_path, cand_path: p.cand_path || '',
    }));
  };

  const buildConfig = () => {
    const auth = { type: form.auth_type };
    if (form.auth_type === 'bearer') auth.token = form.token;
    if (form.auth_type === 'api_key') { auth.api_key = form.api_key; auth.header_name = form.header_name; }
    if (form.auth_type === 'basic') { auth.username = form.username; auth.password = form.password; }
    const mapping = {};
    REQ_FIELDS.forEach((f) => { if (form.req_mapping[f]) mapping[f] = form.req_mapping[f]; });
    const extraction = { mode: form.use_llm ? 'llm' : 'mapping', instructions: form.llm_instructions.trim() };

    if (form.type === 'mcp') {
      const config = {
        server_url: form.base_url.trim(), auth, extraction,
        requirements: { tool: form.mcp_req_tool.trim(), arguments: parseJsonObj(form.mcp_req_args, 'Requirements args') || {}, mapping },
      };
      if (form.pull_candidates) config.candidates = { tool: form.mcp_cand_tool.trim(), arguments: parseJsonObj(form.mcp_cand_args, 'Candidate args') || {}, mapping: {} };
      return config;
    }

    const headers = parseJsonObj(form.headers_json, 'Headers');
    const query = parseJsonObj(form.query_json, 'Query params');
    const body = parseJsonObj(form.body_json, 'Request body');
    const config = {
      base_url: form.base_url.trim(), method: form.method, auth,
      headers,
      requirements: { path: form.req_path.trim(), items_key: form.req_items_key.trim() || undefined, mapping, query, body },
      extraction,
    };
    if (form.pull_candidates) config.candidates = { path: form.cand_path.trim(), items_key: form.cand_items_key.trim() || undefined, mapping: {}, query, body };
    return config;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.base_url.trim()) { setError('Name and Base URL are required.'); return; }
    let config;
    try { config = buildConfig(); } catch (e) { setError(e.message); return; }
    setSaving(true);
    try {
      await createIntegration({
        name: form.name.trim(), type: form.type, provider: form.provider.trim() || null,
        organization_id: form.organization_id || null,
        config,
        capabilities: { requirements: true, candidates: form.pull_candidates },
        sync: { enabled: form.sync_enabled, interval_minutes: Number(form.sync_interval) || 60 },
      });
      setForm(blankForm());
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create connection.');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscover = async () => {
    if (!form.base_url.trim()) { setError('Enter the MCP endpoint URL first.'); return; }
    setDiscovering(true); setError(''); setMcpTools(null);
    try {
      const auth = { type: form.auth_type };
      if (form.auth_type === 'bearer') auth.token = form.token;
      if (form.auth_type === 'api_key') { auth.api_key = form.api_key; auth.header_name = form.header_name; }
      const r = await discoverMcp(form.base_url.trim(), auth);
      if (r.ok) setMcpTools(r.tools || []);
      else setError(`Could not reach MCP server: ${r.error}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Discovery failed.');
    } finally {
      setDiscovering(false);
    }
  };

  const handleTest = async (id) => {
    setTestResult((t) => ({ ...t, [id]: { loading: true } }));
    try {
      const r = await testIntegration(id);
      setTestResult((t) => ({ ...t, [id]: r }));
    } catch (err) {
      setTestResult((t) => ({ ...t, [id]: { ok: false, error: err.response?.data?.detail || 'Test failed' } }));
    }
  };

  const handleSync = async (id) => {
    setTestResult((t) => ({ ...t, [id]: { loading: true } }));
    try {
      const r = await syncIntegration(id);
      const req = r.result?.requirements?.imported ?? 0;
      const cand = r.result?.candidates?.imported ?? 0;
      setTestResult((t) => ({ ...t, [id]: { ok: true, synced: true, msg: `Synced — ${req} requirement(s), ${cand} candidate(s) imported` } }));
      load();
    } catch (err) {
      setTestResult((t) => ({ ...t, [id]: { ok: false, error: err.response?.data?.detail || 'Sync failed' } }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this connection?')) return;
    await deleteIntegration(id);
    load();
  };

  const role = useMemo(decodeRole, []);
  const slackScope = role === 'super_admin' ? 'internal' : 'org';

  return (
    <div className="space-y-8">
      {/* Slack notifications panel — rendered first so it's the first thing
          customers see after "Integrations". The form/test-message UX is
          self-contained inside SlackPanel; backend RBAC enforces who can
          PATCH which scope. */}
      <SlackPanel scope={slackScope} />

      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-ink-100 flex items-center gap-2">
            <Plug className="w-5 h-5 text-brand-400" /> Integrations
          </h2>
          <p className="text-sm text-ink-300 mt-1">
            Pre-configure external sources. Recruiters can then pull Job Requirements (and candidates)
            from them instead of adding everything by hand.
          </p>
        </div>
        <button onClick={() => setShowForm((s) => !s)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-400 transition-colors shrink-0">
          <Plus className="w-4 h-4" /> {showForm ? 'Close' : 'Add connection'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-ink-800 border border-ink-700 rounded-lg p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Connection name *">
              <input value={form.name} onChange={(e) => set('name', e.target.value)} className={inp} placeholder="e.g. Greenhouse — Engineering" />
            </Field>
            <Field label="Type">
              <select value={form.type} onChange={(e) => set('type', e.target.value)} className={inp}>
                {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            {form.type === 'ats' && (
              <Field label="ATS provider (applies a preset)">
                <select value={form.provider} onChange={(e) => applyAtsPreset(e.target.value)} className={inp}>
                  <option value="">Choose a provider…</option>
                  {Object.entries(ATS_PRESETS).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
                  <option value="custom">Other / custom</option>
                </select>
              </Field>
            )}
            {organisations.length > 1 && (
              <Field label="Organization">
                <select value={form.organization_id} onChange={(e) => set('organization_id', e.target.value)} className={inp}>
                  <option value="">Select organization…</option>
                  {organisations.filter((o) => o.organisation_id).map((o) => (
                    <option key={o.organisation_id} value={o.organisation_id}>{o.name}</option>
                  ))}
                </select>
              </Field>
            )}
          </div>

          {form.type === 'ats' && ATS_PRESETS[form.provider] && (
            <p className="text-[11px] text-amber-300/90 -mt-1">
              {ATS_PRESETS[form.provider].note} AI extraction is on (handles the vendor's nested fields).
            </p>
          )}

          <div className="grid sm:grid-cols-3 gap-4">
            <Field label={form.type === 'mcp' ? 'MCP endpoint URL *' : 'Base URL *'} className={form.type === 'mcp' ? 'sm:col-span-2' : 'sm:col-span-2'}>
              <input value={form.base_url} onChange={(e) => set('base_url', e.target.value)} className={inp}
                placeholder={form.type === 'mcp' ? 'https://mcp.vendor.com/mcp' : 'https://api.vendor.com/v1'} />
            </Field>
            {form.type === 'mcp' ? (
              <Field label="Tools">
                <button type="button" onClick={handleDiscover} disabled={discovering}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-ink-700 text-ink-100 text-sm hover:bg-ink-600 disabled:opacity-50">
                  {discovering ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Discover
                </button>
              </Field>
            ) : (
              <Field label="Method">
                <select value={form.method} onChange={(e) => set('method', e.target.value)} className={inp}>
                  <option>GET</option><option>POST</option>
                </select>
              </Field>
            )}
          </div>
          {form.type === 'mcp' && mcpTools && (
            <div className="text-xs text-ink-400 -mt-2">
              {mcpTools.length === 0 ? 'No tools reported by the server.' : (
                <>Available tools: {mcpTools.map((t) => <code key={t.name} className="mx-1 px-1.5 py-0.5 bg-ink-900 rounded text-brand-400">{t.name}</code>)}</>
              )}
            </div>
          )}

          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Auth">
              <select value={form.auth_type} onChange={(e) => set('auth_type', e.target.value)} className={inp}>
                <option value="none">None</option>
                <option value="bearer">Bearer token</option>
                <option value="api_key">API key header</option>
                <option value="basic">Basic auth</option>
              </select>
            </Field>
            {form.auth_type === 'bearer' && (
              <Field label="Token" className="sm:col-span-2"><input value={form.token} onChange={(e) => set('token', e.target.value)} className={inp} /></Field>
            )}
            {form.auth_type === 'api_key' && (<>
              <Field label="Header name"><input value={form.header_name} onChange={(e) => set('header_name', e.target.value)} className={inp} /></Field>
              <Field label="API key"><input value={form.api_key} onChange={(e) => set('api_key', e.target.value)} className={inp} /></Field>
            </>)}
            {form.auth_type === 'basic' && (<>
              <Field label="Username"><input value={form.username} onChange={(e) => set('username', e.target.value)} className={inp} /></Field>
              <Field label="Password"><input type="password" value={form.password} onChange={(e) => set('password', e.target.value)} className={inp} /></Field>
            </>)}
          </div>

          <div className="border-t border-ink-700 pt-4">
            <p className="text-xs font-semibold text-ink-300 mb-2">Requirements {form.type === 'mcp' ? 'tool' : 'endpoint'}</p>
            {form.type === 'mcp' ? (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Tool name"><input value={form.mcp_req_tool} onChange={(e) => set('mcp_req_tool', e.target.value)} className={inp} placeholder="e.g. list_jobs" /></Field>
                <Field label="Tool arguments (JSON)"><input value={form.mcp_req_args} onChange={(e) => set('mcp_req_args', e.target.value)} className={`${inp} font-mono text-xs`} placeholder='{"status":"open"}' /></Field>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Path"><input value={form.req_path} onChange={(e) => set('req_path', e.target.value)} className={inp} placeholder="/jobs or /requirements" /></Field>
                <Field label="Items key (JSON list field)"><input value={form.req_items_key} onChange={(e) => set('req_items_key', e.target.value)} className={inp} placeholder="e.g. jobs (blank = auto-detect)" /></Field>
              </div>
            )}

            {/* AI extraction toggle — LLM maps the source into AIETA's schema at run time */}
            <label className="flex items-start gap-2 text-sm text-ink-200 mt-3">
              <input type="checkbox" checked={form.use_llm} onChange={(e) => set('use_llm', e.target.checked)} className="rounded mt-0.5" />
              <span>
                Use AI extraction
                <span className="block text-xs text-ink-400">An LLM reads the source records and maps them into AIETA's fields at import time — no manual mapping needed. Best for unknown or messy formats.</span>
              </span>
            </label>

            {form.use_llm ? (
              <Field label="Extraction guidance (optional)" className="mt-2">
                <textarea value={form.llm_instructions} onChange={(e) => set('llm_instructions', e.target.value)} rows={2}
                  className={inp} placeholder="e.g. Treat 'position' as the job title; combine city+country into location; only senior roles." />
              </Field>
            ) : (
              <>
                <p className="text-[11px] text-ink-500 mt-3 mb-1">Field mapping — AIETA field ← source field (blank uses sensible defaults)</p>
                <div className="grid sm:grid-cols-4 gap-2">
                  {REQ_FIELDS.map((f) => (
                    <input key={f} value={form.req_mapping[f] || ''} onChange={(e) => setMap(f, e.target.value)}
                      className="px-2 py-1.5 bg-ink-900 border border-ink-700 rounded text-xs text-ink-200"
                      placeholder={f} />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Web-service request shape: custom headers, query params, request body */}
          {form.type !== 'mcp' && (
          <div className="border-t border-ink-700 pt-4">
            <p className="text-xs font-semibold text-ink-300 mb-1">Request (web service)</p>
            <p className="text-[11px] text-ink-500 mb-3">
              Configure the outbound request AIETA sends. Query params append to the URL; the request
              body is sent as JSON when the method is POST/PUT/PATCH (e.g. a search payload).
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Extra headers (JSON)">
                <textarea value={form.headers_json} onChange={(e) => set('headers_json', e.target.value)} rows={3}
                  className={`${inp} font-mono text-xs`} placeholder='{"X-Account": "123"}' />
              </Field>
              <Field label="Query params (JSON)">
                <textarea value={form.query_json} onChange={(e) => set('query_json', e.target.value)} rows={3}
                  className={`${inp} font-mono text-xs`} placeholder='{"status": "open", "limit": 50}' />
              </Field>
              <Field label="Request body (JSON, POST)">
                <textarea value={form.body_json} onChange={(e) => set('body_json', e.target.value)} rows={3}
                  className={`${inp} font-mono text-xs`} placeholder='{"query": {"jobStatus": "active"}}' />
              </Field>
            </div>
          </div>
          )}

          <label className="flex items-center gap-2 text-sm text-ink-300">
            <input type="checkbox" checked={form.pull_candidates} onChange={(e) => set('pull_candidates', e.target.checked)} className="rounded" />
            This connection can also pull candidates
          </label>
          {form.pull_candidates && form.type === 'mcp' && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Candidates tool"><input value={form.mcp_cand_tool} onChange={(e) => set('mcp_cand_tool', e.target.value)} className={inp} placeholder="e.g. list_candidates" /></Field>
              <Field label="Tool arguments (JSON)"><input value={form.mcp_cand_args} onChange={(e) => set('mcp_cand_args', e.target.value)} className={`${inp} font-mono text-xs`} placeholder='{}' /></Field>
            </div>
          )}

          {/* Scheduled auto-sync */}
          <div className="flex items-center gap-3 flex-wrap border-t border-ink-700 pt-4">
            <label className="flex items-center gap-2 text-sm text-ink-300">
              <input type="checkbox" checked={form.sync_enabled} onChange={(e) => set('sync_enabled', e.target.checked)} className="rounded" />
              Auto-sync on a schedule
            </label>
            {form.sync_enabled && (
              <span className="flex items-center gap-2 text-sm text-ink-400">
                every
                <input type="number" min={5} value={form.sync_interval} onChange={(e) => set('sync_interval', e.target.value)}
                  className="w-20 px-2 py-1 bg-ink-900 border border-ink-700 rounded text-sm text-ink-100" />
                minutes
              </span>
            )}
          </div>
          {form.pull_candidates && form.type !== 'mcp' && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Candidates path"><input value={form.cand_path} onChange={(e) => set('cand_path', e.target.value)} className={inp} placeholder="/candidates" /></Field>
              <Field label="Items key"><input value={form.cand_items_key} onChange={(e) => set('cand_items_key', e.target.value)} className={inp} placeholder="blank = auto" /></Field>
            </div>
          )}

          {error && <div className="text-sm text-red-400">{error}</div>}
          <button type="submit" disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-400 disabled:opacity-50">
            {saving ? <><Loader className="w-4 h-4 animate-spin" /> Saving…</> : 'Save connection'}
          </button>
        </form>
      )}

      <div>
        <h3 className="text-sm font-semibold text-ink-100 mb-3">Configured connections</h3>
        {loading ? (
          <p className="text-ink-400 text-sm py-6 text-center">Loading…</p>
        ) : connections.length === 0 ? (
          <p className="text-ink-400 text-sm py-6 text-center bg-ink-800 border border-ink-700 rounded-lg">
            No connections yet. Add one above to let recruiters import from it.
          </p>
        ) : (
          <div className="space-y-3">
            {connections.map((c) => {
              const tr = testResult[c.integration_id];
              return (
                <div key={c.integration_id} className="bg-ink-800 border border-ink-700 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-ink-100">{c.name}</p>
                      <p className="text-xs text-ink-400 mt-0.5">
                        {c.type}{c.provider ? ` · ${c.provider}` : ''} · {c.config?.base_url || 'no URL'} · org {orgName(c.organization_id)}
                      </p>
                      <p className="text-[11px] text-ink-500 mt-0.5">
                        Pulls: {[c.capabilities?.requirements && 'requirements', c.capabilities?.candidates && 'candidates'].filter(Boolean).join(', ') || '—'}
                        {c.sync?.enabled ? ` · auto-sync every ${c.sync.interval_minutes || 60}m` : ' · manual'}
                        {c.last_sync_at ? ` · last sync ${new Date(c.last_sync_at).toLocaleString()}` : ''}
                      </p>
                      {tr && !tr.loading && (
                        <p className={`text-xs mt-2 flex items-center gap-1.5 ${tr.ok ? 'text-green-400' : 'text-red-400'}`}>
                          {tr.ok ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                          {tr.ok ? (tr.synced ? tr.msg : `Reachable — ${tr.sample_count} sample record(s) found`) : `Failed: ${tr.error}`}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => handleTest(c.integration_id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-ink-700 text-ink-100 text-xs hover:bg-ink-600">
                        {tr?.loading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />} Test
                      </button>
                      <button onClick={() => handleSync(c.integration_id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-ink-700 text-ink-100 text-xs hover:bg-ink-600" title="Pull + import now">
                        <RefreshCw className="w-3.5 h-3.5" /> Sync now
                      </button>
                      <button onClick={() => handleDelete(c.integration_id)}
                        className="p-2 rounded-md text-red-400 hover:bg-red-500/10" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const inp = 'w-full px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100 focus:ring-2 focus:ring-brand-500 focus:border-transparent';
function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-ink-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

export default IntegrationsList;
